import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Address autocomplete via OpenStreetMap Nominatim (free, no API key).
 *
 * We picked Nominatim over Google Places because:
 *   - No API key / billing setup — works out-of-the-box in Lovable
 *     preview, Vercel staging, and prod with zero env-var plumbing.
 *   - Generous free tier; the only published rate limit is 1 req/s
 *     per IP, which we comfortably stay under thanks to the 350ms
 *     debounce + 5-char minimum query length.
 *   - Returns full structured addresses in any locale, which is the
 *     feature the recruiter actually cares about (street + city +
 *     country surfaced as a single `display_name`).
 *
 * If we ever need richer data (place_id, components, lat/lng for
 * map pins) we can swap the implementation here without touching
 * any consumer — `AddressSuggestion` already exposes an optional
 * `placeId` field.
 *
 * Nominatim usage policy requires a stable, identifying User-Agent
 * but only when calling from a server. Browser callers are bound by
 * the standard fetch User-Agent which is fine.
 *
 * https://operations.osmfoundation.org/policies/nominatim/
 */

const NOMINATIM_ENDPOINT = 'https://nominatim.openstreetmap.org/search';
const DEBOUNCE_MS = 350;
const MIN_QUERY_LENGTH = 3;
const MAX_RESULTS = 6;

/**
 * Module-level cache: maps the lowercased trimmed query to its
 * suggestions. Trades a few KB of memory for zero re-fetch when the
 * user types, deletes, and re-types the same prefix — a common
 * pattern in onboarding/Settings flows where they tweak the address.
 */
const cache = new Map<string, AddressSuggestion[]>();

export interface AddressSuggestion {
  label: string;
  placeId?: string;
}

interface NominatimResult {
  display_name: string;
  place_id?: number | string;
  /**
   * Structured address components — present when we request
   * `addressdetails=1`. Keys vary by locale and place type, so we
   * walk a priority list when composing the short label.
   */
  address?: {
    house_number?: string;
    road?: string;
    pedestrian?: string;
    footway?: string;
    cycleway?: string;
    path?: string;
    // City-ish variants — Nominatim returns whichever fits the place.
    city?: string;
    town?: string;
    village?: string;
    hamlet?: string;
    municipality?: string;
    suburb?: string;
    city_district?: string;
    postcode?: string;
    country?: string;
  };
}

/**
 * Compose a short, recruiter-friendly address label from Nominatim's
 * structured fields. Format: `Via X N, Città, CAP, Nazione`.
 *
 * Fallback to the full `display_name` if we can't extract enough
 * structure (rare — happens for pure POIs without a postal address).
 *
 * Examples:
 *   "Via Giosuè Carducci 32, Milano, 20123, Italy"
 *   "5 Avenue Anatole France, Paris, 75007, France"
 *   "Schloßstraße 25, Berlin, 12163, Germany"
 */
function composeShortLabel(r: NominatimResult): string {
  const a = r.address;
  if (!a) return r.display_name;

  const street = a.road ?? a.pedestrian ?? a.footway ?? a.cycleway ?? a.path ?? '';
  const streetWithNumber = street && a.house_number
    ? `${street} ${a.house_number}`
    : street;

  const city = a.city ?? a.town ?? a.village ?? a.hamlet ?? a.municipality
    ?? a.suburb ?? a.city_district ?? '';

  const parts = [streetWithNumber, city, a.postcode, a.country].filter(Boolean);
  // Need at least street + city or city + country to be meaningfully
  // shorter than display_name; otherwise just show the full string.
  if (parts.length < 2) return r.display_name;
  return parts.join(', ');
}

export interface UseAddressAutocompleteReturn {
  query: string;
  setQuery: (q: string) => void;
  suggestions: AddressSuggestion[];
  isLoading: boolean;
  open: boolean;
  wrapperRef: React.RefObject<HTMLDivElement>;
  handleChange: (val: string) => void;
  selectSuggestion: (s: AddressSuggestion) => void;
  handleFocus: () => void;
  clear: () => void;
}

export function useAddressAutocomplete(
  value: string,
  onChange: (value: string) => void,
): UseAddressAutocompleteReturn {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  // Holds the timeout id for the in-flight debounce, so a fast typist
  // overwrites the pending fetch instead of triggering N parallel ones.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Aborts the in-flight fetch when a newer query supersedes it; without
  // this, racing responses can flash the wrong suggestion list briefly.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Tear down any pending timer / fetch when the component unmounts —
  // prevents "setState on unmounted component" warnings on fast nav.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    const key = q.trim().toLowerCase();
    if (cache.has(key)) {
      const cached = cache.get(key)!;
      setSuggestions(cached);
      setOpen(cached.length > 0);
      setIsLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setIsLoading(true);
    try {
      // `addressdetails=1` is what gives us the structured fields
      // (road, house_number, city, postcode, country) that we need
      // to compose the short, recruiter-friendly label below.
      const url = `${NOMINATIM_ENDPOINT}?format=json&addressdetails=1&limit=${MAX_RESULTS}&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, {
        signal: ctrl.signal,
        // Nominatim asks for English where possible to keep results
        // consistent across user locales; the user can still type in
        // any language and matches will work.
        headers: { 'Accept-Language': 'en' },
      });
      if (!res.ok) throw new Error(`Nominatim ${res.status}`);
      const data = (await res.json()) as NominatimResult[];
      // Dedupe by short label — Nominatim happily returns the same
      // street with two different POI names, which would surface as
      // visually identical rows after we collapse them.
      const seen = new Set<string>();
      const next: AddressSuggestion[] = [];
      for (const d of data) {
        const label = composeShortLabel(d);
        if (seen.has(label)) continue;
        seen.add(label);
        next.push({ label, placeId: d.place_id != null ? String(d.place_id) : undefined });
      }
      cache.set(key, next);
      setSuggestions(next);
      setOpen(next.length > 0);
    } catch (err) {
      // Abort errors are expected (newer query in flight); only swallow.
      if ((err as { name?: string })?.name === 'AbortError') return;
      // Network errors degrade gracefully — recruiter can still type
      // the address freely, we just don't surface suggestions.
      setSuggestions([]);
      setOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleChange = useCallback((val: string) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < MIN_QUERY_LENGTH) {
      setOpen(false);
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void fetchSuggestions(val);
    }, DEBOUNCE_MS);
  }, [onChange, fetchSuggestions]);

  const selectSuggestion = useCallback((s: AddressSuggestion) => {
    onChange(s.label);
    setOpen(false);
    setSuggestions([]);
  }, [onChange]);

  const handleFocus = useCallback(() => {
    if (value.trim().length >= MIN_QUERY_LENGTH && suggestions.length > 0) {
      setOpen(true);
    }
  }, [value, suggestions]);

  const clear = useCallback(() => {
    onChange('');
    setOpen(false);
    setSuggestions([]);
  }, [onChange]);

  return {
    query: value,
    setQuery: onChange,
    suggestions,
    isLoading,
    open,
    wrapperRef: wrapperRef as React.RefObject<HTMLDivElement>,
    handleChange,
    selectSuggestion,
    handleFocus,
    clear,
  };
}
